import React, { forwardRef, useMemo } from 'react';

const mealOrder = ['Colazione', 'Spuntino mattutino', 'Spuntino', 'Pranzo', 'Spuntino pomeridiano', 'Merenda', 'Cena', 'Dopo cena', 'Spuntino serale', 'Altro'];
const mealAliases = new Map([
  ['spuntino mattina', 'Spuntino mattutino'],
  ['spuntino pomeriggio', 'Spuntino pomeridiano']
]);
const pluralUnits = { porzione: 'porzioni', cucchiaio: 'cucchiai', cucchiaino: 'cucchiaini', tazza: 'tazze', bicchiere: 'bicchieri', pezzo: 'pezzi', fetta: 'fette', vasetto: 'vasetti', mestolo: 'mestoli', piatto: 'piatti' };

function canonicalMeal(value) {
  const meal = value?.trim() || 'Altro';
  return mealAliases.get(meal.toLocaleLowerCase('it-IT')) || meal;
}

export function parseSavedDietRows(plan) {
  const value = plan?.plan ?? plan?.planJson ?? [];
  if (Array.isArray(value)) return value;
  if (typeof value !== 'string' || !value.trim()) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function groupDietRows(rows) {
  const days = new Map();
  rows.filter(row => row?.food?.trim()).forEach(row => {
    const dayNumber = Math.max(1, Number(row.dayNumber) || 1);
    const meal = canonicalMeal(row.meal);
    if (!days.has(dayNumber)) days.set(dayNumber, new Map());
    const meals = days.get(dayNumber);
    if (!meals.has(meal)) meals.set(meal, []);
    meals.get(meal).push(row);
  });
  return [...days.entries()].sort(([a], [b]) => a - b).map(([dayNumber, meals]) => ({
    dayNumber,
    meals: [...meals.entries()]
      .sort(([a], [b]) => {
        const aIndex = mealOrder.indexOf(a);
        const bIndex = mealOrder.indexOf(b);
        return (aIndex < 0 ? mealOrder.length : aIndex) - (bIndex < 0 ? mealOrder.length : bIndex);
      })
      .map(([name, items]) => ({ name, items }))
  }));
}

export function quantityLabel(item) {
  const quantity = item.quantity === '' || item.quantity == null ? '' : String(item.quantity);
  const normalizedQuantity = quantity.trim().replace(',', '.');
  const isExactlyOne = /^1(?:\.0+)?$/.test(normalizedQuantity);
  const unit = quantity && !isExactlyOne && pluralUnits[item.unit] ? pluralUnits[item.unit] : item.unit;
  return [quantity, unit].filter(value => value !== '' && value != null).join(' ');
}

function alternativeOptions(item) {
  if (Array.isArray(item.alternativeOptions) && item.alternativeOptions.length) return item.alternativeOptions;
  return Array.isArray(item.alternatives) ? item.alternatives.map(name => ({ name })) : [];
}

function PrintIcon() {
  return <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M7 8V4h10v4M7 17H5a2 2 0 0 1-2-2v-4a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2h-2"/><path d="M7 14h10v6H7z"/><path d="M17 12h.01"/></svg>;
}

export const PatientDietPlanPreview = forwardRef(function PatientDietPlanPreview({ plan, patient, onClose, onPrint }, ref) {
  const rows = useMemo(() => parseSavedDietRows(plan), [plan]);
  const days = useMemo(() => groupDietRows(rows), [rows]);
  const patientName = [patient?.firstName, patient?.lastName].filter(Boolean).join(' ');
  const period = [plan?.startsOn, plan?.endsOn].filter(Boolean).map(value => new Date(`${value}T00:00:00`).toLocaleDateString('it-IT')).join(' – ');

  return <section id="diet-patient-preview" className="diet-patient-preview-shell" ref={ref} tabIndex="-1" aria-live="polite" aria-labelledby="diet-patient-preview-title">
    <div className="diet-preview-toolbar" data-print-hidden>
      <div><strong>Anteprima per il paziente</strong><span>Le note cliniche riservate non vengono incluse.</span></div>
      <div><button type="button" className="clinic-button secondary" onClick={onClose}>Chiudi</button><button type="button" className="clinic-button" onClick={onPrint}><PrintIcon/>Stampa o salva PDF</button></div>
    </div>

    <article className="diet-patient-preview">
      <header className="diet-preview-cover">
        <div className="diet-preview-brand"><img src="/sibilla-logo.png" alt="Sibilla software nutrizionale" /></div>
        <div className="diet-preview-heading">
          <h2 id="diet-patient-preview-title">{plan?.name || 'Piano alimentare'}</h2>
          <p>Un percorso quotidiano organizzato per pasti, con alternative equivalenti indicate dalla nutrizionista.</p>
        </div>
        <dl>
          <div><dt>Paziente</dt><dd>{patientName || '—'}</dd></div>
          <div><dt>Durata</dt><dd>{days.length} {days.length === 1 ? 'giorno' : 'giorni'}</dd></div>
          {period && <div><dt>Periodo</dt><dd>{period}</dd></div>}
          <div><dt>Metodo</dt><dd>{plan?.planningMethod === 'EnergyTarget' ? 'Obiettivo energetico' : 'Porzioni e frequenze'}</dd></div>
        </dl>
      </header>

      {days.length ? <div className="diet-preview-days">
        {days.map(day => <section className="diet-preview-day" key={day.dayNumber} aria-labelledby={`diet-preview-day-${day.dayNumber}`}>
          <header><span>{String(day.dayNumber).padStart(2, '0')}</span><div><h3 id={`diet-preview-day-${day.dayNumber}`}>Giorno {day.dayNumber}</h3><p>{day.meals.length} momenti alimentari</p></div></header>
          <div className="diet-preview-meals">
            {day.meals.map(meal => <section className="diet-preview-meal" key={meal.name}>
              <h4>{meal.name}</h4>
              <ul>
                {meal.items.map((item, index) => {
                  const alternatives = alternativeOptions(item).filter(option => option?.name?.trim());
                  return <li key={`${item.food}-${index}`}>
                    <div className="diet-preview-prescription"><strong>{item.food}</strong>{quantityLabel(item) && <span>{quantityLabel(item)}</span>}</div>
                    {alternatives.length > 0 && <p><em>Alternative</em>{alternatives.map(option => `${option.name}${quantityLabel(option) ? ` · ${quantityLabel(option)}` : ''}`).join('  /  ')}</p>}
                  </li>;
                })}
              </ul>
            </section>)}
          </div>
        </section>)}
      </div> : <div className="diet-preview-empty"><strong>Piano senza componenti</strong><span>Apri il piano nell’editor e aggiungi almeno un alimento prima di consegnarlo.</span></div>}

      <footer className="diet-preview-footer">
        <p>Seguire il piano secondo le indicazioni ricevute durante la visita. Per dubbi, difficoltà o necessità di sostituzione, contattare la propria nutrizionista.</p>
        <div><span>Data di consegna</span><span>Firma della nutrizionista</span></div>
      </footer>
    </article>
  </section>;
});

export default PatientDietPlanPreview;
